import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { BlogpostSummary, SiteSummary, TagFilter } from '../../models/cms-models';
import { BlogpostsService } from '../../services/blogposts-service';
import { SiteContextService } from '../../services/site-context.service';

@Component({
  selector: 'app-blogposts-page',
  imports: [CommonModule],
  templateUrl: './blogposts-page.html',
  styleUrl: './blogposts-page.css',
})
export class BlogpostsPage implements OnInit {
  private siteContext = inject(SiteContextService);
  private blogpostsService = inject(BlogpostsService);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  sites: SiteSummary[] = [];
  blogposts: BlogpostSummary[] = [];
  selectedSiteId: string | null = null;
  loading = false;
  deletingBlogpostId: string | null = null;
  error: string | null = null;
  tagFilter: TagFilter = {
    tags: [],
    tagMode: 'all',
  };
  private loadedSiteId: string | null = null;

  async ngOnInit(): Promise<void> {
    await this.siteContext.ensureLoaded();
    combineLatest([this.siteContext.sites$, this.siteContext.activeSiteId$])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(([sites, activeSiteId]) => {
        void this.syncSelectedSite(sites, activeSiteId);
      });
  }

  isFilterActive(tag: string): boolean {
    return this.tagFilter.tags.includes(tag);
  }

  createBlogpost(): void {
    if (!this.selectedSiteId || this.deletingBlogpostId) {
      return;
    }

    void this.router.navigate(['/blogposts/new']);
  }

  openBlogpost(blogpostId: string): void {
    if (this.deletingBlogpostId) {
      return;
    }

    void this.router.navigate(['/blogposts', blogpostId]);
  }

  async deleteBlogpost(blogpostId: string): Promise<void> {
    if (!this.selectedSiteId || this.deletingBlogpostId) {
      return;
    }

    const confirmed = window.confirm('Delete this blogpost? This cannot be undone.');
    if (!confirmed) {
      return;
    }

    this.deletingBlogpostId = blogpostId;
    this.error = null;

    try {
      await this.blogpostsService.deleteBlogpost(blogpostId);
      await this.loadBlogposts(this.selectedSiteId);
    } catch {
      this.error = 'Failed to delete the selected blogpost.';
    } finally {
      this.deletingBlogpostId = null;
    }
  }

  async toggleTagFilter(tag: string): Promise<void> {
    const tags = this.tagFilter.tags.includes(tag)
      ? this.tagFilter.tags.filter((existingTag) => existingTag !== tag)
      : [...this.tagFilter.tags, tag];

    this.tagFilter = {
      tags,
      tagMode: 'all',
    };

    await this.reloadSelectedSite();
  }

  async clearFilters(): Promise<void> {
    this.tagFilter = {
      tags: [],
      tagMode: 'all',
    };

    await this.reloadSelectedSite();
  }

  private async syncSelectedSite(sites: SiteSummary[], activeSiteId: string | null): Promise<void> {
    this.sites = sites;

    if (!sites.length) {
      this.selectedSiteId = null;
      this.blogposts = [];
      this.loadedSiteId = null;
      return;
    }

    const targetSiteId = this.siteContext.ensureValidActiveSite(activeSiteId);
    if (!targetSiteId) {
      return;
    }

    this.selectedSiteId = targetSiteId;

    if (this.loadedSiteId === targetSiteId) {
      return;
    }

    this.blogposts = [];
    await this.loadBlogposts(targetSiteId);
  }

  private async loadBlogposts(siteId: string): Promise<void> {
    this.loading = true;
    this.error = null;

    try {
      this.blogposts = await this.blogpostsService.getSiteBlogposts(siteId, this.tagFilter);
      this.loadedSiteId = siteId;
    } catch {
      this.error = 'Failed to load blogposts for the selected site.';
    } finally {
      this.loading = false;
    }
  }

  private async reloadSelectedSite(): Promise<void> {
    if (!this.selectedSiteId) {
      return;
    }

    await this.loadBlogposts(this.selectedSiteId);
  }
}
